/**
 * The frame around every screen a person sees before the application itself:
 * sign in, reset a password, replace a first password, choose a property.
 *
 * One frame so the four stay one family — the lit ground, the mark, a heading
 * and one card — and look like the application they lead into rather than
 * like a form that happens to be on the same server.
 *
 * The mark is the hotel's: its logo, drawn whole above the heading as the rail
 * draws it, and its name as the heading unless a screen has its own. Nobody is
 * signed in yet, so both come from the public `/branding`; until it answers
 * the header holds its place rather than flashing EREG first.
 */

import type { ReactNode } from 'react'
import { BrandMark, Card } from '@/components/ui/primitives'
import { HotelLogo } from '@/components/domain/HotelLogo'
import { useBranding, useDocumentTitle } from '@/lib/branding'
import { cn } from '@/lib/utils'

export function AuthFrame({
  title,
  subtitle,
  titleAs: Title = 'div',
  action,
  footer,
  children,
  className,
}: {
  /** The screen's own heading. Without one, the hotel's name. */
  title?: string
  subtitle?: ReactNode
  /** `h1` when the frame's title is the page's heading; otherwise the card has it. */
  titleAs?: 'h1' | 'div'
  /** Top-end of the header: the theme switch, or a way to sign out. */
  action?: ReactNode
  footer?: ReactNode
  children: ReactNode
  className?: string
}) {
  const branding = useBranding()
  const hotel = branding?.name || null
  const logo = branding?.logo_url ?? null
  useDocumentTitle(branding && hotel)

  // A hotel may go by its logo alone; the logo then says the name, and EREG
  // under it would be wrong. The heading stays for screen readers.
  const unnamed = !title && !hotel && logo !== null

  const heading = (
    <div className="min-w-0">
      <Title
        className={cn(
          'truncate text-[17px] font-semibold leading-tight tracking-[-0.01em]',
          unnamed && 'sr-only',
        )}
      >
        {title ?? hotel ?? 'EREG'}
      </Title>
      {subtitle && <p className="truncate text-[12.5px] text-[var(--text-muted)]">{subtitle}</p>}
    </div>
  )
  const end = action && <div className="ms-auto shrink-0">{action}</div>

  return (
    <div className="ground grid min-h-screen place-items-center bg-[var(--bg)] px-4 py-10">
      <div className={cn('w-full max-w-[26rem]', className)}>
        {logo ? (
          <div className="mb-6">
            <div className="flex items-start gap-3">
              <HotelLogo src={logo} surface="theme" className="h-12 w-auto max-w-[18rem]" />
              {end}
            </div>
            <div className="mt-4">{heading}</div>
          </div>
        ) : (
          // `invisible` while /branding is still answering: the space is kept,
          // and nobody sees EREG turn into the hotel's name a moment later.
          <div className={cn('mb-6 flex items-center gap-3', !branding && 'invisible')}>
            <BrandMark size={44} />
            {heading}
            {end}
          </div>
        )}

        <Card variant="glow" className="p-7 shadow-[var(--shadow-lg)]">
          {children}
        </Card>

        {footer && (
          <div className="mt-5 text-center text-[12px] text-[var(--text-muted)]">{footer}</div>
        )}
      </div>
    </div>
  )
}
